import { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { getMergeQueuesUrl } from 'src/components/merge-queues/constants';
import { StatsigFeatureKeys } from 'src/features';
import { useFlag } from 'src/hooks/flag';
import { useStatsigGate } from 'src/hooks/statsig';
import { useMutation } from 'src/utils/fb-query';
import { authRequest, jsonHeaders } from 'src/utils/fetch';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { messages } from '../merge-queues.i18n';
import {
  MergeQueueEditFormState,
  MergeQueueFormState,
  MergeQueueEditPayload,
  MergeQueuePayload,
  MergeQueueResponse,
  UseMergeQueueActionParams,
} from '../types';
import { formValuesToEditMergeQueue, formValuesToMergeQueue } from '../utils';

const createMergeQueueRequest = async (
  payload: MergeQueuePayload,
  owner: string,
  slug: string
): Promise<MergeQueueResponse> => {
  const url = getMergeQueuesUrl(owner, slug);

  const response = await fetch(
    authRequest(url, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    })
  );

  if (!response.ok) {
    throw new Error(
      `Failed to create merge queue: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

const editMergeQueueRequest = async (
  payload: MergeQueueEditPayload,
  owner: string,
  slug: string,
  mergeQueueUuid: string
) => {
  const url = `${getMergeQueuesUrl(owner, slug)}/${mergeQueueUuid}`;

  const response = await fetch(
    authRequest(url, {
      method: 'PUT',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    })
  );

  if (!response.ok) {
    throw new Error(
      `Failed to edit merge queue: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

export const useCreateEditMergeQueue = ({
  mergeQueueUuid,
  owner,
  slug,
  onClose,
  onSuccess,
}: UseMergeQueueActionParams) => {
  const { showSuccessFlag, showErrorFlag } = useFlag();
  const { formatMessage } = useIntl();
  const isMergeQueuesLandingAreaEnabled = useStatsigGate(
    StatsigFeatureKeys.isMergeQueuesLandingAreaEnabled
  );
  const isMergeQueuesStrategyEnabled = useStatsigGate(
    StatsigFeatureKeys.isMergeQueuesStrategyEnabled
  );

  const createMergeQueueCallback = useCallback(
    (payload: MergeQueueFormState) => {
      const mergeQueuePayload = formValuesToMergeQueue(payload, {
        isMergeQueuesLandingAreaEnabled,
        isMergeQueuesStrategyEnabled,
      });
      return createMergeQueueRequest(mergeQueuePayload, owner, slug);
    },
    [isMergeQueuesLandingAreaEnabled, isMergeQueuesStrategyEnabled, owner, slug]
  );

  const { mutate: createMergeQueue, isPending: isCreatingMergeQueue } =
    useMutation<MergeQueueResponse, MergeQueueFormState>(
      createMergeQueueCallback,
      {
        onSuccess: created => {
          showSuccessFlag(formatMessage(messages.createSuccess));
          onSuccess?.(created);
        },
        onError: (error: Error) => {
          captureExceptionWithTags(error, {
            section: 'merge-queue-configuration',
            action: 'create-merge-queue',
          });
          showErrorFlag(formatMessage(messages.createError));
        },
        onFinished: () => {
          onClose();
        },
      }
    );

  const editMergeQueueCallback = useCallback(
    (payload: MergeQueueEditFormState) => {
      const mergeQueuePayload = formValuesToEditMergeQueue(payload);
      return editMergeQueueRequest(
        mergeQueuePayload,
        owner,
        slug,
        mergeQueueUuid || payload.mergeQueueUuid || ''
      );
    },
    [mergeQueueUuid, owner, slug]
  );

  const { mutate: editMergeQueue, isPending: isEditingMergeQueue } =
    useMutation(editMergeQueueCallback, {
      onSuccess: () => {
        showSuccessFlag(formatMessage(messages.editSuccess));
        onSuccess?.();
      },
      onError: (error: Error) => {
        captureExceptionWithTags(error, {
          section: 'merge-queue-configuration',
          action: 'edit-merge-queue',
        });
        showErrorFlag(formatMessage(messages.editError));
      },
      onFinished: () => {
        onClose();
      },
    });

  return {
    createMergeQueue,
    isCreatingMergeQueue,
    editMergeQueue,
    isEditingMergeQueue,
  };
};
